import CopyWithTooltip from 'components/CopyWithTooltip';
import CreateOAuthUrlModal from './createOAuthUrlModal';
import CustomSnackBar from '../audienceManager/customSnackBar';
import DataTable from 'components/DataTable/DataTable';
import GhostLoader from 'react-ghost-loader';
import Header from 'components/Header';
import MultiSelect from 'components/MultiSelect';
import OAuthUrlViewModal from './oauthUrlViewModal';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, IconButton } from '@attain-sre/attain-design-system';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as Styled from './index.styles';
// Material UI Components
import Box from '@material-ui/core/Box';
import TextFieldSearch from 'components/TextFieldSearch/TextFieldSearch';
import moment from 'moment';
import tokens from 'tokens';
import {
  ADVERTISER_ID,
  ALL_EMAILS,
  CLICK_TO_VIEW_FULL_OAUTH_URL,
  CLIENT_NAME,
  COPY_OAUTH_URL,
  CREATED_AT,
  CREATED_BY,
  CREATE_OAUTH_URL,
  CREDENTIAL_GENERATED_AT,
  DESCRIPTION,
  OAUTH_PLATFORMS_FILTER,
  OAUTH_URL,
  OAUTH_URLS,
  PLATFORM,
} from '../../content';
import { CaretDown, Copy, File, Plus } from '@phosphor-icons/react';
import { OAUTH_PLATFORM_LABELS, OAUTH_PLATFORM_OPTIONS } from './constants';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';
import {
  fetchOAuthUrls,
  removeNotification,
  resetCreateOAuthUrlState,
} from './slice';
import { getOAuthUrlAdvertiserId, sortOAuthUrls } from '../../utils/oauthUrls';

const inputs = createTheme({
  overrides: {
    MuiFormControl: {
      root: {
        maxHeight: 48,
      },
    },
    MuiFormLabel: {
      root: {
        maxHeight: 48,
      },
    },
    MuiInputLabel: {
      root: {
        marginTop: -5,
        '&$focused': {
          marginTop: 0,
        },
      },
    },
    MuiInputBase: {
      root: {
        maxHeight: 48,
      },
      input: {
        borderRadius: 10,
        alignSelf: 'center',
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 10,
      },
      input: {
        padding: '12px 14px',
      },
    },
    MuiButton: {
      root: {
        borderRadius: 10,
      },
      outlinedPrimary: {
        color: tokens.colorButtonPrimary,
      },
      containedPrimary: {
        backgroundColor: tokens.colorButtonPrimary,
        '&:hover': {
          backgroundColor: tokens.colorButtonPrimary,
        },
      },
    },
    MuiButtonBase: {
      root: {
        borderRadius: 10,
      },
    },
  },
});

const OAuthUrls = () => {
  const dispatch = useAppDispatch();
  const { uiOAuthManagerVisible = false } = useFlags();
  const [platformFilter, setPlatformFilter] = useState<string[]>([]);
  const [emailFilter, setEmailFilter] = useState<string>('all');
  const [advertiserIdSearch, setAdvertiserIdSearch] = useState<string>('');
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedOAuthUrl, setSelectedOAuthUrl] = useState<string>('');

  const oauthUrls = useAppSelector(
    (state) => state.oauthUrlsReducer?.entities?.oauthUrls || []
  );
  const loading = useAppSelector(
    (state) => state.oauthUrlsReducer.ui.loading.state === 'loading'
  );
  const notifications = useAppSelector(
    (state) => state.oauthUrlsReducer.ui.notifications
  );

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // Sorting state
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  useEffect(() => {
    if (uiOAuthManagerVisible) {
      dispatch(fetchOAuthUrls());
    }
  }, [dispatch, uiOAuthManagerVisible]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [platformFilter, emailFilter, advertiserIdSearch]);

  const dataTableKey = useMemo(() => {
    return `${platformFilter.join(',')}-${emailFilter}-${advertiserIdSearch}`;
  }, [platformFilter, emailFilter, advertiserIdSearch]);

  // Extract unique emails from oauthUrls
  const emailOptions = useMemo(() => {
    const uniqueEmails = new Set<string>();
    oauthUrls?.forEach((url) => {
      if (url.createdBy) {
        uniqueEmails.add(url.createdBy);
      }
    });
    const emails = Array.from(uniqueEmails)
      .sort()
      .map((email) => ({ id: email, label: email }));
    return [{ id: 'all', label: 'All Emails' }, ...emails];
  }, [oauthUrls]);

  // Filter and sort OAuth URLs (UI-side only)
  const filteredAndSortedOAuthUrls = useMemo(() => {
    let filtered = oauthUrls || [];

    // Filter by platform
    if (platformFilter.length > 0 && !platformFilter.includes('all')) {
      filtered = filtered.filter((url) =>
        platformFilter.includes(url.platform)
      );
    }

    // Filter by email
    if (emailFilter && emailFilter !== 'all') {
      filtered = filtered.filter((url) => url.createdBy === emailFilter);
    }

    // Filter by advertiser ID (contains, case-insensitive)
    if (advertiserIdSearch.trim()) {
      const searchLower = advertiserIdSearch.toLowerCase().trim();
      filtered = filtered.filter((url) => {
        const advertiserId = getOAuthUrlAdvertiserId(url);
        return (
          advertiserId &&
          advertiserId?.toString()?.toLowerCase()?.includes(searchLower)
        );
      });
    }

    // Sort (UI-side)
    return sortOAuthUrls(filtered, sortBy, sortOrder);
  }, [
    oauthUrls,
    platformFilter,
    emailFilter,
    advertiserIdSearch,
    sortBy,
    sortOrder,
  ]);

  // Paginate (UI-side)
  const paginatedOAuthUrls = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredAndSortedOAuthUrls.slice(startIndex, endIndex);
  }, [filteredAndSortedOAuthUrls, page, rowsPerPage]);

  const handleCreateClick = async () => {
    await dispatch(resetCreateOAuthUrlState());
    setOpenCreateModal(true);
  };

  const handleCreateSuccess = () => {
    setOpenCreateModal(false);
  };

  const handleViewClick = (oauthUrl: string) => {
    setSelectedOAuthUrl(oauthUrl);
    setOpenViewModal(true);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (value: number) => {
    setRowsPerPage(value);
    setPage(0);
  };

  const handleSortChange = (sortInfo: { column: string }) => {
    let newSortOrder: 'ASC' | 'DESC' = 'DESC';
    if (sortBy === sortInfo.column) {
      newSortOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';
    }
    setSortBy(sortInfo.column);
    setSortOrder(newSortOrder);
    setPage(0);
  };

  const columns = [
    {
      title: CLIENT_NAME,
      column: 'clientName',
      width: '20%',
      isSorted: sortBy === 'clientName',
      sortOrder: sortBy === 'clientName' ? sortOrder : undefined,
    },
    {
      title: DESCRIPTION,
      column: 'description',
      width: '17%',
      isSorted: sortBy === 'description',
      sortOrder: sortBy === 'description' ? sortOrder : undefined,
    },
    {
      title: PLATFORM,
      column: 'platform',
      width: '5%',
      isSorted: sortBy === 'platform',
      sortOrder: sortBy === 'platform' ? sortOrder : undefined,
    },
    {
      title: ADVERTISER_ID,
      column: 'advertiserId',
      width: '12%',
      isSorted: sortBy === 'advertiserId',
      sortOrder: sortBy === 'advertiserId' ? sortOrder : undefined,
    },
    {
      title: OAUTH_URL,
      column: 'oauthUrl',
      width: '6%',
      isSortDisabled: true,
    },
    {
      title: CREATED_BY,
      column: 'createdBy',
      width: '18%',
      isSorted: sortBy === 'createdBy',
      sortOrder: sortBy === 'createdBy' ? sortOrder : undefined,
    },
    {
      title: CREATED_AT,
      column: 'createdAt',
      width: '8%',
      isSorted: sortBy === 'createdAt',
      sortOrder: sortBy === 'createdAt' ? sortOrder : undefined,
      rightAlign: true,
    },
    {
      title: CREDENTIAL_GENERATED_AT,
      column: 'credentialsGeneratedAt',
      width: '13%',
      isSorted: sortBy === 'credentialsGeneratedAt',
      sortOrder: sortBy === 'credentialsGeneratedAt' ? sortOrder : undefined,
      rightAlign: true,
    },
  ];

  const formattedRows = paginatedOAuthUrls.map((oauthUrl) => {
    const advertiserId = getOAuthUrlAdvertiserId(oauthUrl);
    const platformLabel =
      OAUTH_PLATFORM_LABELS[oauthUrl.platform] || oauthUrl.platform;

    return (
      <>
        <td>
          <Styled.ClientName title={oauthUrl.clientName || ''}>
            {oauthUrl.clientName}
          </Styled.ClientName>
        </td>
        <td>
          <Styled.Description title={oauthUrl.description || ''}>
            {oauthUrl.description || '-'}
          </Styled.Description>
        </td>
        <td>
          <Styled.Platform>{platformLabel}</Styled.Platform>
        </td>
        <td>
          <Styled.AdvertiserId>{advertiserId}</Styled.AdvertiserId>
        </td>
        <td>
          <Styled.OAuthUrlCell>
            <IconButton
              onClick={() => handleViewClick(oauthUrl.oauthUrl)}
              title={CLICK_TO_VIEW_FULL_OAUTH_URL}
            >
              <File size={20} />
            </IconButton>
            <CopyWithTooltip value={oauthUrl.oauthUrl}>
              <IconButton>
                <Copy size={20} />
              </IconButton>
            </CopyWithTooltip>
          </Styled.OAuthUrlCell>
        </td>
        <td>
          <Styled.CreatedBy>{oauthUrl.createdBy}</Styled.CreatedBy>
        </td>
        <td>
          <Styled.CreatedAt>
            {oauthUrl.createdAt ? (
              <>
                <div>{moment(oauthUrl.createdAt).format('MM/DD/YYYY')}</div>
                <div>{moment(oauthUrl.createdAt).format('hh:mm A')}</div>
              </>
            ) : (
              '-'
            )}
          </Styled.CreatedAt>
        </td>
        <td>
          <Styled.CredentialGeneratedAt>
            {oauthUrl.credentialsGeneratedAt ? (
              <>
                <div>
                  {moment(oauthUrl.credentialsGeneratedAt).format('MM/DD/YYYY')}
                </div>
                <div>
                  {moment(oauthUrl.credentialsGeneratedAt).format('hh:mm A')}
                </div>
              </>
            ) : (
              '-'
            )}
          </Styled.CredentialGeneratedAt>
        </td>
      </>
    );
  });

  const platformOptions = useMemo(() => {
    return OAUTH_PLATFORM_OPTIONS.filter((opt) => opt.id !== 'all');
  }, []);

  // Handle platform filter change - "All" clears selections
  const handlePlatformFilterChange = (value: string[]) => {
    // If "all" is in the selection, clear everything
    if (value.includes('all')) {
      setPage(0);
      setPlatformFilter([]);
      return;
    }
    // Remove "all" if it exists and set the filter
    const filtered = value.filter((v) => v !== 'all');
    setPage(0);
    setPlatformFilter(filtered);
  };

  if (!uiOAuthManagerVisible) {
    return null;
  }

  return (
    <>
      <Box style={{ width: '100%' }}>
        <Header
          title={OAUTH_URLS}
          centerBorder={true}
          action={
            <ThemeProvider theme={inputs}>
              <Button
                variant="contained"
                onClick={handleCreateClick}
                startIcon={<Plus />}
                style={{ fontSize: '16px' }}
              >
                {CREATE_OAUTH_URL}
              </Button>
            </ThemeProvider>
          }
        />

        <Box height="100%">
          <Styled.SearchArea>
            <ThemeProvider theme={inputs}>
              <Styled.SearchTextField>
                <TextFieldSearch
                  placeholder={`Search ${ADVERTISER_ID}`}
                  value={advertiserIdSearch}
                  onChange={(e) => setAdvertiserIdSearch(e.target.value)}
                  disabled={loading}
                  style={{
                    width: '100%',
                  }}
                />
              </Styled.SearchTextField>
            </ThemeProvider>
            <Styled.MultiSelectWrapper>
              <MultiSelect
                options={[{ id: 'all', label: 'All' }, ...platformOptions]}
                value={platformFilter}
                onChange={handlePlatformFilterChange}
                placeholder={OAUTH_PLATFORMS_FILTER}
                tagLimit={1}
              />
            </Styled.MultiSelectWrapper>
            <Styled.SelectAutoCompleteEmail
              disablePortal
              id="select-oauth-email"
              options={emailOptions}
              popupIcon={<CaretDown />}
              value={emailOptions.find((a) => a.id === emailFilter) || null}
              renderInput={(params) => (
                <TextFieldSearch
                  {...params}
                  placeholder={ALL_EMAILS}
                  variant="outlined"
                />
              )}
              onChange={(e, newValue: any) => {
                if (newValue?.id) {
                  setEmailFilter(newValue.id);
                }
              }}
              renderOption={(props, option: any) => (
                <Styled.SelectAutoCompleteGroupOptions>
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                </Styled.SelectAutoCompleteGroupOptions>
              )}
            />
          </Styled.SearchArea>

          {loading && filteredAndSortedOAuthUrls.length === 0 ? (
            <div style={{ padding: '24px 32px', background: '#F0EFF1' }}>
              <GhostLoader height="400px" width="100%" />
            </div>
          ) : (
            <div style={{ padding: '24px 32px', background: '#F0EFF1' }}>
              <DataTable
                key={dataTableKey}
                loading={loading}
                columns={columns}
                rows={(formattedRows as any) || []}
                showRowsPerPageDropdown
                customRowsPerPageOptions={[25, 50, 100]}
                defaultRowsPerPage={25}
                onPaginationClick={handleChangePage as any}
                onRowsPerPageChange={handleChangeRowsPerPage as any}
                onSortChange={handleSortChange as any}
                totalRows={filteredAndSortedOAuthUrls.length}
              />
            </div>
          )}
        </Box>
      </Box>

      <CreateOAuthUrlModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      <OAuthUrlViewModal
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
        oauthUrl={selectedOAuthUrl}
      />

      {notifications.map(({ state, message }, i) => (
        <CustomSnackBar
          index={i}
          trigger={true}
          severity={
            state === 'done'
              ? 'success'
              : state === 'warning'
              ? 'warning'
              : 'error'
          }
          message={message || ''}
          key={i}
          removeNotificationFunction={() => dispatch(removeNotification(i))}
        />
      ))}
    </>
  );
};

export default OAuthUrls;
